import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { useLocalStorage } from "@src/appV2/lib/utils/useLocalstorage";
import { useModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useState } from "react";
import Confetti from "react-confetti";

import {
  getScholarshipStorageKey,
  SCHOLARSHIP_RESPONSES,
  type ScholarshipResponse,
} from "./constants";
import { ScholarshipBanner } from "./ScholarshipBanner";
import { ScholarshipBottomSheet } from "./ScholarshipBottomSheet";

export function ScholarshipEntrypoint() {
  const bottomSheetModalState = useModalState();
  const { userId } = useDefinedWorker();
  const { showInfoToast } = useToast();
  const [showConfetti, setShowConfetti] = useState(false);

  const isEnabled = useCbhFlag(CbhFeatureFlag.CONCORDE_SCHOLARSHIP, {
    defaultValue: false,
  });

  const storageKey = getScholarshipStorageKey(userId);

  const [scholarshipResponse, setScholarshipResponse] = useLocalStorage<
    ScholarshipResponse | undefined
  >(storageKey, undefined);

  const shouldShowBanner = isEnabled && !isDefined(scholarshipResponse);

  useLogEffect(
    APP_V2_USER_EVENTS.CONCORDE_SCHOLARSHIP_V2_BANNER_VIEWED,
    { workerId: userId },
    { enabled: shouldShowBanner }
  );

  const handleBannerClick = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.CONCORDE_SCHOLARSHIP_V2_BANNER_OPENED, {
      workerId: userId,
    });
    bottomSheetModalState.openModal();
  }, [userId, bottomSheetModalState]);

  const handleBannerDismiss = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.CONCORDE_SCHOLARSHIP_V2_BANNER_DISMISSED, {
      workerId: userId,
    });
    setScholarshipResponse(SCHOLARSHIP_RESPONSES.NOT_INTERESTED);
  }, [userId, setScholarshipResponse]);

  const handleSubmit = useCallback(
    (response: ScholarshipResponse) => {
      setScholarshipResponse(response);

      if (response === SCHOLARSHIP_RESPONSES.INTERESTED) {
        setShowConfetti(true);
      }

      showInfoToast("Thanks for letting us know!");
    },
    [setScholarshipResponse, showInfoToast]
  );

  if (!shouldShowBanner && !showConfetti) {
    return null;
  }

  return (
    <>
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          numberOfPieces={400}
          gravity={0.8}
          recycle={false}
          onConfettiComplete={() => {
            setShowConfetti(false);
          }}
        />
      )}
      {shouldShowBanner && (
        <>
          <ScholarshipBanner onClick={handleBannerClick} onDismiss={handleBannerDismiss} />
          <ScholarshipBottomSheet
            modalState={bottomSheetModalState}
            workerId={userId}
            onSubmit={handleSubmit}
          />
        </>
      )}
    </>
  );
}
